import { ActionIcon, Menu, Text } from '@mantine/core';
import { IconDotsVertical, IconShoppingCartHeart, IconSalt, IconScale } from '@tabler/icons-react';
import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { PantryItemDrawer } from '../Pantry/PantryItemDrawer';
import { createDisplayName, normalizeWithAliases, suggestPantryCategory } from '../../utils/itemNormalization';
import { PantryCategory } from '../../types/pantry';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { toggleGroceryListItems } from '../../services/krogerService';
import { useMediaQuery } from '@mantine/hooks';
import { pantryItemCoversRequirement } from '../../utils/unitConversion';

type GroceryItemActionsProps = {
  itemName: string;
  quantity?: number;
  unit?: string;
  category?: string;
  mealPlanId?: string;
  requiredQuantity?: number;
  requiredUnit?: string;
  onSuccess?: () => void;
  onGroceryListUpdate?: (updatedList: any) => void;
  // Shopping preferences props
  hasProductPreference?: boolean;
  isKrogerConnected?: boolean;
  cartAdditionCompleted?: boolean;
  onToggleProductPreference?: (itemName: string) => void;
  isFirstItem?: boolean; // For tour highlighting
  alreadyHave?: boolean; // Current state of the grocery item
  // Portal the menu to the body (default true). False inside mobile bottom
  // sheets, where body portals get mispositioned.
  menuWithinPortal?: boolean;
  // When set, "Add with quantity" delegates to the host (which renders the
  // pantry form as a pushed view) instead of opening a nested drawer
  onAddWithQuantity?: () => void;
};

export const GroceryItemActions = ({
  itemName,
  quantity,
  unit,
  category,
  mealPlanId,
  requiredQuantity,
  requiredUnit,
  onSuccess,
  onGroceryListUpdate,
  hasProductPreference = false,
  isKrogerConnected = false,
  cartAdditionCompleted = false,
  onToggleProductPreference,
  isFirstItem = false,
  alreadyHave = false,
  menuWithinPortal = true,
  onAddWithQuantity
}: GroceryItemActionsProps) => {
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Mobile breakpoint
  const isMobile = useMediaQuery('(max-width: 768px)');

  const checkAndMarkGroceryItem = async (savedItem: any) => {
    if (!user || !mealPlanId || !requiredQuantity || !requiredUnit) {
      return;
    }

    try {
      if (pantryItemCoversRequirement(savedItem, requiredQuantity, requiredUnit)) {
        await markGroceryItemAsHave();
      }
    } catch (error) {
      console.error('Error checking grocery item sufficiency:', error);
    }
  };
  
  const markGroceryItemAsHave = async () => {
    if (!user || !mealPlanId) return;

    // Only toggle if item is NOT already marked as "have"
    // This prevents un-toggling items that are already checked
    if (alreadyHave) {
      return;
    }

    try {
      const token = await user.getIdToken();
      const result = await toggleGroceryListItems(mealPlanId, [itemName], token);

      // Update the grocery list if we have a callback and successful result
      if (result.success && result.groceryList && onGroceryListUpdate) {
        onGroceryListUpdate(result.groceryList);
      }
    } catch (error) {
      console.error('Error marking grocery item as have:', error);
    }
  };

  const handleAddAsStaple = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!user) return;

    try {
      setLoading(true);
      const token = await user.getIdToken();
      
      const payload = {
        name: normalizeWithAliases(itemName),
        displayName: createDisplayName(itemName),
        type: 'staple',
        category: (category as PantryCategory) || suggestPantryCategory(itemName),
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pantry`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add staple');
      }

      const responseData = await response.json();
      
      // Check if we should mark the grocery list item as "already have" first
      if (mealPlanId && responseData.item) {
        await checkAndMarkGroceryItem(responseData.item);
      }
      
      // Call success callback after grocery list is updated
      onSuccess?.();
    } catch (error: any) {
      console.error('Error adding staple:', error);
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to add staple',
        color: 'red',
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddAsIngredient = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onAddWithQuantity) {
      // Host renders the pantry form itself (e.g. as a pushed view inside
      // the shopping list drawer) instead of a drawer-on-drawer
      onAddWithQuantity();
    } else {
      setModalOpen(true);
    }
  };

  const handleModalSave = async (savedItem: any) => {
    // Set loading state when modal save completes
    setLoading(true);
    setModalOpen(false);
    
    try {
      // Check if we should mark the grocery list item as "already have" first
      if (mealPlanId && requiredQuantity && requiredUnit && savedItem) {
        await checkAndMarkGroceryItem(savedItem);
      }
      
      // Call success callback after grocery list is updated
      onSuccess?.();
    } finally {
      // Clear loading state after a brief delay to show the spinner
      setTimeout(() => setLoading(false), 300);
    }
  };

  const handleDuplicateFound = (existingItem: any) => {
    modals.openConfirmModal({
      title: 'Item already in pantry',
      children: (
        <Text size="sm">
          <strong>{existingItem.displayName}</strong> is already in your pantry.
          <br /><br />
          Current quantity: {existingItem.quantity ? `${existingItem.quantity} ${existingItem.unit}` : 'Staple item'}
          <br /><br />
          Would you like to edit the existing item?
        </Text>
      ),
      labels: { confirm: 'Edit existing item', cancel: 'Cancel' },
      confirmProps: { color: 'orange' },
      onConfirm: () => {
        // Close current modal and trigger success to potentially refresh parent
        setModalOpen(false);
        onSuccess?.();
        notifications.show({
          title: 'Navigate to pantry',
          message: 'Go to my pantry to edit this item.',
          color: 'blue',
          autoClose: 4000,
        });
      },
      onCancel: () => {
        setModalOpen(false);
      },
    });
  };

  const handleShoppingPreferences = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onToggleProductPreference) {
      onToggleProductPreference(itemName);
    }
  };

  // Determine if shopping preferences should be available
  const showShoppingPreferences = isKrogerConnected && !cartAdditionCompleted;

  return (
    <>
      <Menu position="bottom-end" shadow="md" withinPortal={menuWithinPortal}>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              loading={loading}
              style={{ pointerEvents: 'auto' }}
              onClick={(e) => e.stopPropagation()}
              data-tour={isFirstItem ? 'grocery-item-pantry' : undefined}
            >
              <IconDotsVertical size={20} />
            </ActionIcon>
          </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>Add to my pantry</Menu.Label>
          <Menu.Item
            leftSection={<IconSalt size={isMobile ? 18 : 14} stroke={1.5} />}
            onClick={handleAddAsStaple}
            style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
          >
            Add as staple
          </Menu.Item>
          <Menu.Item
            leftSection={<IconScale size={isMobile ? 18 : 14} stroke={1.5} />}
            onClick={handleAddAsIngredient}
            style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
          >
            Add with quantity
          </Menu.Item>
          
          {showShoppingPreferences && (
            <>
              <Menu.Divider />
              <Menu.Label>Shopping</Menu.Label>
              <Menu.Item
                leftSection={<IconShoppingCartHeart size={isMobile ? 18 : 14} stroke={1.5} />}
                onClick={handleShoppingPreferences}
                style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
              >
                {hasProductPreference ? 'Edit shopping preferences' : 'Add shopping preferences'}
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>

      <PantryItemDrawer
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleModalSave}
        onDuplicateFound={handleDuplicateFound}
        prefillName={itemName}
        prefillQuantity={quantity}
        prefillUnit={unit}
        prefillCategory={category}
        prefillType="ingredient"
      />
    </>
  );
};
